import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Formik, Form, Field } from 'formik';
import * as Yup from "yup";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { toast } from 'react-toastify';
import { AddCookie, getValueFromCookie } from "../../../utils/cookies";
import { postCall } from "../../../Api/axios";
import Button from "../../Shared/Button";
import AuthActionCard from "../AuthActionCard/AuthActionCard";

const CssTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: "2px !important",
    height: "35px !important",
    "& fieldset": {
      borderColor: "d9d9d9",
    },
    "&:hover fieldset": {
      borderColor: "#1890ff",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1890ff",
    },
  },
});

export default function Login() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (getValueFromCookie("token")) {
      navigate("/orders");
    }
  }, [navigate]);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const initialValues = {
    email: "",
    password: "",
  };

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    password: Yup.string()
      .required("Password is required"),
  });

  const handleLogin = async (values, { setSubmitting }) => {
    const url = "/api/v2/dashboard/login";
    try {
      const res = await postCall(url, values);
      handleRedirect(res.message.token, res.message.user);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message || "Login failed");
      } else {
        toast.error(error.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRedirect = (token, user) => {
    const { _id } = user;
    AddCookie("token", token);
    localStorage.setItem("user_id", _id);
    localStorage.setItem("user", JSON.stringify(user));
    navigate("/orders");
  };

  const loginForm = (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleLogin}
    >
      {({ isSubmitting, touched, errors }) => (
        <Form className="m-auto w-10/12 md:w-3/4">
          {/* Email */}
          <div className="py-1">
            <label
              htmlFor="email"
              className="text-sm py-2 ml-1 font-medium text-left text-[#606161] inline-block"
            >
              Email<span className="text-[#FF0000]"> *</span>
            </label>
            <Field
              as={CssTextField}
              name="email"
              type="email"
              placeholder="Enter Email"
              autoComplete="off"
              className="w-full h-full px-2.5 py-3.5 text-[#606161] bg-transparent !border-black"
              size="small"
              error={touched.email && Boolean(errors.email)}
              helperText={touched.email && errors.email}
            />
          </div>

          {/* Password */}
          <div className="py-1">
            <label
              htmlFor="password"
              className="text-sm py-2 ml-1 font-medium text-left text-[#606161] inline-block"
            >
              Password<span className="text-[#FF0000]"> *</span>
            </label>
            <div className="w-full flex relative">
              <Field
                as={CssTextField}
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter Password"
                autoComplete="off"
                className="w-full h-full px-2.5 py-3.5 text-[#606161] bg-transparent relative"
                size="small"
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
              />
              <IconButton
                aria-label="toggle password visibility"
                onClick={togglePasswordVisibility}
                className="absolute right-2 top-1/2 transform -translate-y-1/2"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </div>
          </div>

          {/* Submit button */}
          <div className="py-3 pt-6 text-center flex flex-row justify-center">
            <Button
              isloading={isSubmitting ? 1 : 0}
              disabled={isSubmitting}
              variant="contained"
              title="Login"
              className="!w-40 !capitalize !py-2"
              type="submit"
              sx={{ backgroundColor: "#1890ff" }}
            />
          </div>
        </Form>
      )}
    </Formik>
  );

  const navigation_link = (
    <div className="py-2 text-center">
      <NavLink to="/forgot-password" className="">
        <p className="text-xs text-[#3d629ad2] hover:text-[#0066ffd2]">
          Forgot password
        </p>
      </NavLink>
    </div>
  );

  return <AuthActionCard action_form={loginForm} navigation_link={navigation_link} />;
}
